import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const BusinessCard = ({ business }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getESGScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-50';
    if (score >= 80) return 'text-blue-600 bg-blue-50';
    if (score >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-orange-600 bg-orange-50';
  };

  const getFocusIcon = (focus) => {
    switch (focus) {
      case 'Environmental': return 'Leaf';
      case 'Social': return 'Users';
      case 'Governance': return 'Shield';
      default: return 'Building2';
    }
  };

  return (
    <motion.div
      className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
      whileHover={{ y: -4 }}
    >
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
              <img 
                src={business?.logo} 
                alt={business?.logoAlt || `${business?.name} company logo`}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{business?.name}</h3>
              <p className="text-sm text-muted-foreground">{business?.industry}</p>
            </div>
          </div>
          
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${getESGScoreColor(business?.esgScore)}`}>
            {business?.esgScore}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {business?.description}
        </p>

        {/* ESG Focus Areas */}
        <div className="flex flex-wrap gap-2 mb-4">
          {business?.esgFocus?.map((focus, index) => (
            <div 
              key={index}
              className="flex items-center space-x-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium"
            >
              <Icon name={getFocusIcon(focus)} size={12} />
              <span>{focus}</span>
            </div>
          ))}
        </div>

        {/* Recent Initiatives Preview */}
        {business?.recentInitiatives?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center">
              <Icon name="TrendingUp" size={14} className="mr-1" />
              Recent Initiatives
            </h4>
            <div className="space-y-1">
              {business?.recentInitiatives?.slice(0, 2)?.map((initiative, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="CheckCircle" size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">{initiative}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Users" size={16} className="text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Engagement</p>
              <p className="text-sm font-semibold text-foreground">
                {business?.communityEngagement?.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Star" size={16} className="text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Rating</p>
              <p className="text-sm font-semibold text-foreground">
                {business?.stakeholderRating}/5.0
              </p>
            </div>
          </div>
        </div>

        {/* Expand Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full mt-4 py-2 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 transition-colors flex items-center justify-center space-x-2"
        >
          <span>{isExpanded ? 'Show Less' : 'View Full Profile'}</span>
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
        </button>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="px-6 pb-6 border-t border-border"
        >
          {/* Company Details */}
          <div className="pt-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center">
                <Icon name="MapPin" size={14} className="mr-1" />
                Location & Size
              </h4>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">📍 {business?.location}</p>
                <p className="text-sm text-muted-foreground">👥 {business?.companySize} Enterprise</p>
              </div>
            </div>

            {/* Sustainability Goals */}
            {business?.sustainabilityGoals?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center">
                  <Icon name="Target" size={14} className="mr-1" />
                  Sustainability Goals
                </h4>
                <ul className="space-y-1">
                  {business?.sustainabilityGoals?.map((goal, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{goal}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button className="flex-1 py-2 px-4 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2">
                <Icon name="ExternalLink" size={14} />
                <span>Visit Website</span>
              </button>
              <button className="flex-1 py-2 px-4 bg-background border border-border text-foreground rounded-lg text-sm font-medium hover:bg-muted transition-colors flex items-center justify-center space-x-2">
                <Icon name="Share2" size={14} />
                <span>Share</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default BusinessCard;